import { APIError } from "@certn/api";
import { PrimaryTag } from "@certn/cascadia-react";
import { ActionListControl } from "@certn/forms/src/controls/ActionListControl";
import { ArrayControlProps } from "@jsonforms/core";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import { updateSection, removeItems, replaceOrAddItems } from "../../utils";

const getIn = (value: any, keys: (string | number)[]) =>
  keys.reduce((acc, key) => acc?.[key], value);

type CredentialItem = {
  in_progress?: boolean;
  issue_date?: string | null;
};

/**
 * Inline (client-side) validation for a single credential before it is saved.
 *
 * The backend enforces "issue_date is required unless in_progress" via an
 * InterClaimTypeConstraint on the CREDENTIAL claim type. However, individual
 * credentials are saved with validate=false (so the applicant can save a
 * credential before meeting the minimum-count requirement), which means that
 * conditional requirement would otherwise only surface as a non-field-mapped
 * 400 toast when the applicant clicks "Next step" -- leaving them stuck with no
 * indication of what to fix (FR-294). Returning errors here keeps the edit
 * modal open with the message attached to the issue_date field.
 */
export function getCredentialInlineErrors(
  values: CredentialItem,
  t: TFunction
): { issue_date: string[] } | undefined {
  if (!values.in_progress && !values.issue_date) {
    return {
      issue_date: [
        t(
          "Please provide the issue date or indicate that you are still working on this credential."
        ),
      ],
    };
  }

  return undefined;
}

export function CredentialHistoryControl(props: ArrayControlProps) {
  const { caseId } = useApplicationFormContext();
  const { currentSection, basePath } = useApplication();
  const { t } = useTranslation();
  const dataSection = ["credential_history", "credentials"];

  return (
    <ActionListControl
      {...props}
      alwaysDisplayRequirementsAsBullets
      renderTag={({ in_progress, expiration_date }) => {
        const now = new Date();
        const isExpired = expiration_date && new Date(expiration_date) < now;

        return (
          <>
            {in_progress ? (
              <PrimaryTag type="subtle">{t("In progress")}</PrimaryTag>
            ) : null}
            {isExpired ? (
              <PrimaryTag type="warn">{t("Expired")}</PrimaryTag>
            ) : null}
          </>
        );
      }}
      onItemChanged={async (data, values, index) => {
        // Block saving a credential that is not in progress but has no issue
        // date (FR-294) so the applicant sees an inline error on issue_date
        // instead of getting stuck on a non-field-mapped 400 at "Next step".
        const inlineErrors = getCredentialInlineErrors(
          values as CredentialItem,
          t
        );
        if (inlineErrors) {
          return inlineErrors;
        }

        const updatedArray = replaceOrAddItems(
          dataSection,
          data,
          values,
          index
        );
        if (caseId) {
          try {
            // Use validate=false to allow saving individual credentials before
            // meeting the minimum count requirement. Full validation (including
            // minimum count and issue_date requirements) happens when clicking
            // "Next step" to complete the section.
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              false,
              basePath
            );
          } catch (e) {
            if (e instanceof APIError) {
              return getIn(e.body, [...dataSection, index]);
            }
          }
        }
      }}
      onItemRemoved={async (parentValues, indexes) => {
        const updatedArray = removeItems(dataSection, parentValues, indexes);
        if (updatedArray) {
          if (caseId) {
            await updateSection(
              caseId,
              updatedArray,
              currentSection,
              false,
              basePath
            );
          }
        }
      }}
    />
  );
}
